import { getOwnCompanyData } from 'API/company';
import { Roles } from 'enums';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import useCountries from 'hooks/use-countries';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import useErrorStore from 'stores/error-store';
import { Company } from '../../../types/company.types';

export interface CompanyInformationForm {
  company: string;
  street: string;
  zip: string;
  city: string;
  country: OptionType | null;
}

const defaultForm: CompanyInformationForm = {
  company: '',
  street: '',
  zip: '',
  city: '',
  country: null,
};

const useCompanyInfoForm = () => {
  const { role } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [company, setCompany] = useState<Company>();
  const [companyInfo, setCompanyInfo] = useState<CompanyInformationForm>(defaultForm);

  const { countryOptions, fetchCountries } = useCountries();

  const fetchOwnCompany = async () => {
    const res = await getOwnCompanyData();
    if (!res.error) {
      const resCompany = new Company(res.data);

      setCompany(resCompany);

      const initCompany: CompanyInformationForm = {
        company: resCompany.name ?? '',
        street: resCompany.addressLine1 ?? '',
        zip: resCompany.postalCode ?? '',
        city: resCompany.city ?? '',
        country: null,
      };
      setCompanyInfo(initCompany);
    } else {
      handleOpenErrorDialog(res?.error, '/tenants/current', 'get');
    }
  };

  useEffect(() => {
    if (role === Roles.ADMIN || role === Roles.AGENCY || role === Roles.MANAGER || role === Roles.OWNER) {
      fetchOwnCompany();
      fetchCountries();
    }
  }, [role]);

  useEffect(() => {
    if (!_.isNil(company?.country)) {
      const selectedCountry = countryOptions.find((option) => option.value === company.country) || null;
      setCompanyInfo((prev) => ({ ...prev, country: selectedCountry }));
    }
  }, [company, countryOptions]);

  const handleCompanyInfoChange = (field: keyof CompanyInformationForm) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setCompanyInfo((prev) => ({ ...prev, [field]: event.target.value }));
    };
  };

  const handleCountryChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setCompanyInfo((prev) => ({ ...prev, country: newValue }));
  };

  const resetCompanyInfoForm = () => {
    setCompanyInfo(defaultForm);
  };

  return {
    company: companyInfo.company,
    street: companyInfo.street,
    zip: companyInfo.zip,
    city: companyInfo.city,
    country: companyInfo.country,
    handleCompanyInfoChange,
    handleCountryChange,
    resetCompanyInfoForm,
    countryOptions,
  };
};

export default useCompanyInfoForm;
